// Meeting rows — one row per meeting per person, found by the meeting's key.
//
// Each person keeps their OWN meeting rows (under their connection_views row); the two
// phones' rows for one meeting share its key, minted by the deciding phone
// (meetingRule.js). The key replaces the old one-row-per-calendar-day check (a racy
// read-then-insert on the UTC date): the database's unique (view, key) index makes a
// retried exchange a no-op.
//
// The venue gate lives HERE, not in callers: a meeting row never stores a venue the
// person didn't pick (Mark, 2026-10-16 — the system suggests, never picks). Rows carry
// no confirmation flag, so their readers can't gate; this module is the only writer.
//
// Old rows (before the key, 2026-10-17) have a NULL key. They are never backfilled; a
// venue write for a person whose record has no key goes to their newest key-less row.
//
// Permanent data layer: no React — carries to the native app.

import { supabase } from '../supabase';
import { SOURCE_BRANCH } from '../../utils/deployInfo';
import { gateVenueMetadata } from '../../utils/locationFormatters';
import { nightKey } from '../moments/nightKey';
import { currentMeetingRow } from './meetingRule';

const MEETING_COLUMNS =
  'id, meeting_key, via, in_person, venue_name, venue_address, met_date, met_at, met_utc_offset, location_data, created_by, created_at';

const instantOf = (row) => Date.parse(row.met_at || row.met_date || row.created_at || '') || 0;

/** Newest meeting first (the true instant; legacy date-only rows by their date). */
const newestFirst = (rows) =>
  [...rows].sort(
    (a, b) =>
      instantOf(b) - instantOf(a) || Date.parse(b.created_at || 0) - Date.parse(a.created_at || 0)
  );

/** All of one person's meetings for a connection, newest first. */
export const getMeetingsForConnection = async (chatRoomId, userId) => {
  // maybeSingle: a view row can legitimately be absent (device-local connection
  // not present in this backend) — render falls back to connectionMetadata
  const { data, error } = await supabase
    .from('connection_views')
    .select(`meetings(${MEETING_COLUMNS})`)
    .eq('chat_room_id', chatRoomId)
    .eq('user_id', userId)
    .maybeSingle();

  if (error) {
    console.warn('[MEETINGS] getMeetingsForConnection failed:', error.message);
    return [];
  }
  return newestFirst(data?.meetings || []);
};

/**
 * Create a person's meeting row for one meeting (idempotent on the key).
 * Called after their connection_views row exists (userDataStorage.upsertConnectionView).
 *
 * @param {string} viewId - connection_views.id
 * @param {string} userId - the person's profile or guest id
 * @param {Object} metadata - their connection record; must carry meetingKey + meetingDate
 */
export const seedMeeting = async (viewId, userId, metadata) => {
  if (!metadata?.meetingKey) {
    throw new Error('seedMeeting: a meeting row needs its meeting key');
  }
  const venueInfo = gateVenueMetadata(metadata).locationData?.venueInfo || null;
  const { error } = await supabase.from('meetings').upsert(
    {
      connection_view_id: viewId,
      meeting_key: metadata.meetingKey,
      via: metadata.via || null,
      created_by: userId,
      venue_name: venueInfo?.venue?.name || null,
      venue_address: venueInfo?.address?.formattedAddress || null,
      // The night, not the UTC calendar date (a late-night meeting kept its own day)
      met_date: nightKey(metadata.meetingDate, metadata.meetingUtcOffset) || null,
      met_at: metadata.meetingDate,
      // Number.isFinite, not truthy — 0 (UTC) is a valid offset
      met_utc_offset: Number.isFinite(metadata.meetingUtcOffset) ? metadata.meetingUtcOffset : null,
      location_data: venueInfo,
      source_branch: SOURCE_BRANCH,
    },
    { onConflict: 'connection_view_id,meeting_key', ignoreDuplicates: true }
  );
  if (error) console.warn('[MEETINGS] seed failed:', error.message);
};

/**
 * A place that arrived after the meeting row was written (a late GPS fix, or the host's
 * confirmed venue reaching the guest). Fills what's empty, never overwrites: the
 * meeting's first location stays its first location, and a person's own pick wins.
 * The venue is gated here — an unconfirmed suggestion fills only the area.
 *
 * @param {string} chatRoomId
 * @param {string} userId - the row owner's id
 * @param {Object} metadata - the record the place came in (exchangeType, venueConfirmed,
 *   locationData, meetingKey)
 */
export const fillMeetingLocationIfEmpty = async (chatRoomId, userId, metadata) => {
  const venueInfo = gateVenueMetadata(metadata)?.locationData?.venueInfo;
  // Nothing placeable (a gated record keeps `venue: null` — truthy, but empty) → no read
  const hasPlace =
    venueInfo?.venue?.name ||
    venueInfo?.address?.formattedAddress ||
    venueInfo?.address?.neighborhood ||
    venueInfo?.address?.city;
  if (!chatRoomId || !userId || !hasPlace) return;
  try {
    const rows = await getMeetingsForConnection(chatRoomId, userId);
    const row = currentMeetingRow(rows, metadata.meetingKey);
    if (!row) return;
    const patch = {};
    if (!row.location_data) patch.location_data = venueInfo;
    if (!row.venue_name && venueInfo.venue?.name) patch.venue_name = venueInfo.venue.name;
    if (!row.venue_address && venueInfo.address?.formattedAddress) {
      patch.venue_address = venueInfo.address.formattedAddress;
    }
    if (!Object.keys(patch).length) return;
    const { error } = await supabase.from('meetings').update(patch).eq('id', row.id);
    if (error) console.warn('[MEETINGS] location fill failed:', error.message);
  } catch (err) {
    console.warn('[MEETINGS] location fill error:', err?.message);
  }
};

/** Set one meeting's venue, by row (signup D1: any meeting's venue is editable). */
export const updateMeetingVenue = async (meetingId, venueInfo) => {
  if (!meetingId) return;
  const { error } = await supabase
    .from('meetings')
    .update({
      venue_name: venueInfo?.venue?.name || null,
      venue_address: venueInfo?.address?.formattedAddress || null,
      location_data: venueInfo,
    })
    .eq('id', meetingId);
  if (error) console.warn('[MEETINGS] updateMeetingVenue failed:', error.message);
};

/**
 * A person's confirm or pick for the meeting their record describes — the row with its
 * key, or (a record from before keys) their newest key-less row.
 */
export const setMeetingVenue = async (chatRoomId, userId, meetingKey, venueInfo) => {
  const rows = await getMeetingsForConnection(chatRoomId, userId);
  const row = currentMeetingRow(rows, meetingKey);
  if (!row) {
    console.warn('[MEETINGS] setMeetingVenue: no meeting row to update');
    return;
  }
  await updateMeetingVenue(row.id, venueInfo);
};
